import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";
import {
  DIAL_START_ANGLE,
  DIAL_SWEEP,
  angleToRatio,
  pointToAngle,
  ratioToValue,
  valueToRatio,
} from "./dial-math";

interface ThermostatDialProps {
  /** Current target temperature. */
  value: number;
  /** Measured temperature, shown as secondary text. */
  measured?: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
  onCommit?: (value: number) => void;
  className?: string;
}

const SIZE = 176;
const STROKE = 12;
const R = (SIZE - STROKE) / 2;
const ARC_FRACTION = DIAL_SWEEP / 360;

export function ThermostatDial({
  value,
  measured,
  min = 5,
  max = 30,
  step = 0.5,
  unit = "°",
  disabled,
  onChange,
  onCommit,
  className,
}: ThermostatDialProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  // Kept for future drag styling; pointer capture is the move guard.
  const [_dragging, setDragging] = useState(false);
  const latest = useRef(value);
  const ratio = valueToRatio(value, min, max);

  // Keep the drag baseline in sync when the parent moves the value (e.g. socket update).
  useEffect(() => {
    latest.current = value;
  }, [value]);

  const valueFromPointer = (clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return value;
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    // Dead zone: taps near the center are ambiguous, keep the current value.
    if (dx * dx + dy * dy < (R * 0.3) ** 2) return latest.current;
    const angle = pointToAngle(0, 0, dx, dy);
    return ratioToValue(angleToRatio(angle), min, max, step);
  };

  return (
    <div
      data-slot="thermostat-dial"
      className={cn(
        "relative inline-grid place-items-center rounded-full has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <svg
        ref={svgRef}
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="slider"
        aria-label="Target temperature"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${value}${unit}`}
        tabIndex={disabled ? -1 : 0}
        className="touch-none outline-none"
        onPointerDown={(e) => {
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(true);
          latest.current = valueFromPointer(e.clientX, e.clientY);
          onChange(latest.current);
        }}
        onPointerMove={(e) => {
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
          latest.current = valueFromPointer(e.clientX, e.clientY);
          onChange(latest.current);
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          e.currentTarget.releasePointerCapture(e.pointerId);
          setDragging(false);
          onCommit?.(latest.current);
        }}
        onPointerCancel={() => {
          setDragging(false);
          onCommit?.(latest.current);
        }}
        onKeyDown={(e) => {
          const delta =
            e.key === "ArrowUp" || e.key === "ArrowRight"
              ? step
              : e.key === "ArrowDown" || e.key === "ArrowLeft"
                ? -step
                : 0;
          if (delta === 0) return;
          e.preventDefault();
          e.stopPropagation();
          const next = Math.min(max, Math.max(min, value + delta));
          latest.current = next;
          onChange(next);
          onCommit?.(next);
        }}
      >
        <g transform={`rotate(${DIAL_START_ANGLE} ${SIZE / 2} ${SIZE / 2})`}>
          {/* Track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray={`${ARC_FRACTION} 1`}
            className="stroke-muted"
          />
          {/* Fill — hidden at zero, a round linecap would still draw a dot. */}
          {ratio > 0.001 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              strokeWidth={STROKE}
              strokeLinecap="round"
              pathLength={1}
              strokeDasharray={`${ARC_FRACTION * ratio} 1`}
              className="stroke-domain-climate transition-[stroke-dasharray] duration-75"
            />
          )}
        </g>
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-semibold tabular-nums">
          <AnimatedNumber value={value} precision={1} />
          <span className="text-lg font-normal text-muted-foreground">{unit}</span>
        </span>
        {measured !== undefined && (
          <span className="text-xs text-muted-foreground tabular-nums">
            Now {measured.toFixed(1)}
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}
